import { useCallback } from 'react';
import { apiService } from '../services/api';
import {
  dispatchStaticPostGeneration,
  pollStaticPostStatus,
} from '../services/staticPostGeneration';
import type { StaticPostGenerateRequest, ImageOption } from '../types/staticPost';
import { useGenerationJobs } from './useGenerationJobs';

/**
 * Job state for the async static post generation flow. No longer block on
 * the POST response — after dispatch the job is "pending" and this hook
 * polls the status endpoint until done/error/timeout. Mirrors
 * useCarouselGeneration.ts: state is tracked per post (so several posts can
 * generate at once), while polling itself is keyed by campaign_id.
 *
 * imageOptions/selectedImageIndex on 'done' are set only when the quality
 * gate exhausted every attempt — a caveat on an otherwise-done job (see
 * StaticPostGenerateResponse), not a distinct status.
 */
export type StaticPostGenerationState =
  | { status: 'idle' }
  | { status: 'pending'; campaignId: string }
  | {
      status: 'done';
      campaignId: string;
      outputPath: string | null;
      content: string | null;
      imageOptions?: ImageOption[] | null;
      selectedImageIndex?: number | null;
    }
  | { status: 'error'; campaignId: string; errorMessage: string }
  | { status: 'timeout'; campaignId: string };

export interface StaticPostGenerationCallbacks {
  onDone?: (
    campaignId: string,
    outputPath: string | null,
    content: string | null,
    imageOptions?: ImageOption[] | null,
    selectedImageIndex?: number | null,
    meta?: { alreadyGenerated?: boolean }
  ) => void | Promise<void>;
  onError?: (campaignId: string, message: string) => void | Promise<void>;
  onTimeout?: (campaignId: string) => void | Promise<void>;
}

const IDLE: StaticPostGenerationState = { status: 'idle' };

export function useStaticPostGeneration() {
  const { jobs, setJob, stopPolling, trackPolling, clear } = useGenerationJobs<StaticPostGenerationState>();

  const poll = useCallback(
    (postId: string, campaignId: string, callbacks: StaticPostGenerationCallbacks) => {
      const stop = pollStaticPostStatus(apiService, {
        campaignId,
        onDone: async (outputPath, content, imageOptions, selectedImageIndex) => {
          setJob(postId, { status: 'done', campaignId, outputPath, content, imageOptions, selectedImageIndex });
          await callbacks.onDone?.(campaignId, outputPath, content, imageOptions, selectedImageIndex);
        },
        onError: async (message) => {
          setJob(postId, { status: 'error', campaignId, errorMessage: message });
          await callbacks.onError?.(campaignId, message);
        },
        onTimeout: async () => {
          setJob(postId, { status: 'timeout', campaignId });
          await callbacks.onTimeout?.(campaignId);
        },
      });
      trackPolling(postId, stop);
    },
    [setJob, trackPolling]
  );

  const start = useCallback(
    async (request: StaticPostGenerateRequest, callbacks: StaticPostGenerationCallbacks = {}) => {
      const postId = String(request.post_id || '');
      // Replaces only this post's previous job — other posts keep generating.
      stopPolling(postId);
      setJob(postId, { status: 'pending', campaignId: '' });

      let dispatched;
      try {
        dispatched = await dispatchStaticPostGeneration(apiService, request);
      } catch (err: any) {
        const message = err?.message || 'Could not start generation. Please try again.';
        // No campaign_id yet — callbacks get '' as the campaign.
        setJob(postId, { status: 'error', campaignId: '', errorMessage: message });
        await callbacks.onError?.('', message);
        return;
      }

      // Guard: the post already has an image and force_regenerate wasn't set.
      const alreadyGenerated =
        dispatched.response &&
        'error_code' in dispatched.response &&
        dispatched.response.error_code === 'ALREADY_GENERATED';
      if (alreadyGenerated) {
        const existing = dispatched.response as import('../types/staticPost').StaticPostAlreadyGeneratedResponse;
        const cid = dispatched.campaignId || '';
        if (existing.output_path) {
          setJob(postId, { status: 'done', campaignId: cid, outputPath: existing.output_path, content: null });
          await callbacks.onDone?.(cid, existing.output_path, null, null, null, { alreadyGenerated: true });
        } else {
          const message = existing.message || 'This post already has a generated image.';
          setJob(postId, { status: 'error', campaignId: cid, errorMessage: message });
          await callbacks.onError?.(cid, message);
        }
        return;
      }

      const campaignId = dispatched.campaignId;
      if (!campaignId) {
        const message = 'Generation could not be started (no campaign ID returned).';
        setJob(postId, { status: 'error', campaignId: '', errorMessage: message });
        await callbacks.onError?.('', message);
        return;
      }

      setJob(postId, { status: 'pending', campaignId });
      poll(postId, campaignId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const retry = useCallback(
    async (request: StaticPostGenerateRequest, callbacks: StaticPostGenerationCallbacks = {}) => {
      await start({ ...request, force_regenerate: true }, callbacks);
    },
    [start]
  );

  // Resumes polling for a job that's already running server-side (e.g. the
  // user refreshed the page mid-generation) without dispatching a new one.
  const resume = useCallback(
    (postId: string, campaignId: string, callbacks: StaticPostGenerationCallbacks = {}) => {
      stopPolling(postId);
      setJob(postId, { status: 'pending', campaignId });
      poll(postId, campaignId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const stateFor = (postId?: string | null): StaticPostGenerationState => (postId && jobs[postId]) || IDLE;

  return {
    stateFor,
    isPendingFor: (postId?: string | null) => stateFor(postId).status === 'pending',
    isErrorFor: (postId?: string | null) => stateFor(postId).status === 'error',
    isTimedOutFor: (postId?: string | null) => stateFor(postId).status === 'timeout',
    errorMessageFor: (postId?: string | null) => {
      const state = stateFor(postId);
      return state.status === 'error' ? state.errorMessage : null;
    },
    start,
    retry,
    resume,
    clear,
  };
}
